import { Component, EventEmitter, Input, Output } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";
import { User } from "@aptsi-types";
import { UserService } from "../user.service";
import { RoleService } from "../../roles/role.service";
import { SuccessDialogComponent } from "../../shared/dialogs/success-dialog/success-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { KeycloakStateService } from 'src/app/core/service/utility.service';

@Component({
  selector: "app-user-create-or-update",
  templateUrl: "./user-create-or-update.component.html",
  styleUrls: ["./user-create-or-update.component.scss"],
})
export class UserCreateOrUpdateComponent {
  @Input({ required: true }) isCreateMode: boolean = true;
  @Input({ required: true }) userId: string | null = null;

  @Output() cancel = new EventEmitter<void>();
  @Output() submit = new EventEmitter<void>();

  showPassword: boolean = false;
  form: FormGroup;
  user: User = null;
  organizationId: string;

  constructor(private userService: UserService, private roleService: RoleService, private dialog: MatDialog, private router: Router, private keycloakStateService: KeycloakStateService) {

  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  async ngOnInit() {
    this.organizationId = this.keycloakStateService.getOrganizationId();
    await this.fetchRoles();

    if (this.isCreateMode) {
      this.initializeForm(); // initialize blank form for create
    } else {
      await this.fetchUserById(); // fetch user and then initialize form
      this.initializeForm();      // now that this.user is set
    }
  }

  initializeForm() {
    if (this.isCreateMode) {
      this.form = new FormGroup({
        firstName: new FormControl(null, Validators.required),
        lastName: new FormControl(null, Validators.required),
        email: new FormControl(null, [Validators.required, Validators.email]),
        role: new FormControl("None", [Validators.required, this.typeValidator]),
        username: new FormControl(null, Validators.required),
        password: new FormControl(null, Validators.required),
      });
    } else {
      this.form = new FormGroup({
        userId: new FormControl(this.userId, Validators.required),
        firstName: new FormControl(this.user.firstName, Validators.required),
        lastName: new FormControl(this.user.lastName, Validators.required),
        email: new FormControl(this.user.email, [
          Validators.required,
          Validators.email,
        ]),
        role: new FormControl(this.user.roleName, [
          Validators.required,
          this.typeValidator,
        ]),
        username: new FormControl(this.user.username, Validators.required),
        password: new FormControl(this.user.password),
      });
    }
  }

  async fetchUserById(): Promise<void> {
    try {
      const user = await this.userService.getUser(this.userId, this.organizationId).toPromise();
      this.user = user;
    } catch (error) {
      console.error("Failed to fetch user by ID", error);
    }
  }


  roleOptions: string[] = ["None"];

  typeValidator(control: AbstractControl): { [key: string]: any } | null {
    return control.value === "None" ? { noneSelected: true } : null;
  }

  async fetchRoles() {
    try {
      const response = await this.roleService.getAllRoles(this.organizationId).toPromise();
      const roleNames = response.map((role: any) => role.name);  // Extract `name` from each role
      this.roleOptions = ["None", ...roleNames];  // Set updated list

      console.log("Role Options Are", this.roleOptions);
    } catch (error) {
      console.error("Failed to load roles", error);
    }
  }




  onSubmit(): void {
    if (this.form.invalid) return;


    if (this.isCreateMode) {
      // Call service to create
      this.userService.createUser(
        this.form.value.firstName,
        this.form.value.lastName,
        this.form.value.email,
        this.form.value.role,
        this.form.value.username,
        this.form.value.password,
        this.organizationId
      ).subscribe(() => {
        this.openSuccessDialog(true); // Only open after success
      });
    } else {
      // Call service to update
      this.userService
        .updateUser(
          this.form.value.userId,
          this.form.value.firstName,
          this.form.value.lastName,
          this.form.value.email,
          this.form.value.role,
          this.form.value.username,
          this.form.value.password,
          this.organizationId
        ).subscribe(() => {
          this.openSuccessDialog(false); // Only open after success
        });
    }
  }

  openSuccessDialog(isCreateMode: boolean): void {
    this.dialog
      .open(SuccessDialogComponent, {
        width: "250px",
        data: { isCreateMode, title: "User" },
      })
      .afterClosed()
      .subscribe(() => {
        this.submit.emit();
      });
  }


  onCancel(): void {
    this.cancel.emit();
  }
}
